import type { Vec2, Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import type { CastleTier } from './shipTemplates';
import {
  WALE_HALF,
  castleTiers,
  deckAt,
  hullPoint,
  loftRows,
  tierDeckAt,
  tierRailAt,
  type FormedTemplate,
} from './shipForm';
import { loftSolid, type Ring3 } from './shipSolids';
import { sideStrips } from './shipTimber';
import { buildFittings } from './shipFittings';
import { girthFractions, girthLength, plankingStyle } from './shipPatterns';
import {
  CAP_RAIL,
  castleHalfWidth,
  hullStylesOf,
  samplesOver,
  stationsOf,
  type HullPalette,
  type HullStyles,
  type ShipTier,
} from './hullParts';

/**
 * A formed hull in one of three tiers (docs/plans/ships.md 4). The far tier closes the hull at
 * the rail and paints its wales as bands; the mid and near tiers sink the deck under bulwarks and
 * stand the wales proud, and add the fittings. Every part is a closed solid facing outward.
 */

export type { ShipTier, HullPalette, HullStyles } from './hullParts';

/** How far a wale stands out of the planking. */
export const WALE_PROUD = 0.12;
const WALE_GRIP = 0.05;
const CASTLE_SINK = 0.3;
const TIER_STEP = 0.05;
const BULWARK = { thickness: 0.12, sink: 0.15, set: 0.03 } as const;

/* Keel, starboard rows up, across the top, port rows down: +X crossed with the tangent points out. */
function hullRing(template: FormedTemplate, s: number, rows: readonly number[]): Ring3 {
  const keel = hullPoint(template, s, 0);
  const side = rows.map((u) => hullPoint(template, s, u));
  const port = [...side].reverse().map(([x, y, z]): Vec3 => [x, y, -z]);
  return [[keel[0], keel[1], 0], ...side, ...port];
}

function isWaleBand(template: FormedTemplate, lo: number, hi: number): boolean {
  return template.form.wales.some(
    (w) => Math.abs(lo - (w - WALE_HALF)) < 1e-9 && Math.abs(hi - (w + WALE_HALF)) < 1e-9
  );
}

/* The hull's styles with the planking of its sides. */
interface BodyStyles extends HullStyles {
  readonly planking: VertexStyle;
}

/* The strip from ring point k to k + 1 spans rows (lo, hi) on either side, or the top: the sides
   are planked but for the wales the far tier paints as bands, the top is the deck. */
function bandStyles(
  template: FormedTemplate,
  rows: readonly number[],
  styles: BodyStyles,
  banded: boolean
): (k: number) => VertexStyle {
  const u = [0, ...rows];
  const n = rows.length;
  return (k) => {
    if (k === n) return styles.hull;
    const i = k < n ? k : 2 * n - k;
    return banded && isWaleBand(template, u[i]!, u[i + 1]!) ? styles.wale : styles.planking;
  };
}

/* The keel and every loft row up the starboard side to the rail at station s. */
function girthSection(template: FormedTemplate, s: number): Vec3[] {
  const keel = hullPoint(template, s, 0);
  return [[keel[0], keel[1], 0], ...loftRows(template).map((u) => hullPoint(template, s, u))];
}

/* Ring point k of `rows` at station s in the planking: metres from the stem along the hull and
   the girth fraction round the whole section to the rail, so the tiers that stop at the deck lay
   their strakes where the far tier does. */
function plankingUv(
  template: FormedTemplate,
  stations: readonly number[],
  rows: readonly number[]
): (r: number, k: number) => Vec2 {
  const all = loftRows(template);
  const fractions = stations.map((s) => girthFractions(girthSection(template, s)));
  const n = rows.length;
  return (r, k) => {
    const row = k === 0 ? -1 : k <= n ? k - 1 : 2 * n - k;
    const u = row < 0 ? 0 : rows[row]!;
    const v = row < 0 ? 0 : fractions[r]![1 + all.indexOf(u)]!;
    return [hullPoint(template, stations[r]!, u)[0] + template.length / 2, v];
  };
}

function buildBody(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  styles: BodyStyles
): void {
  const all = loftRows(template);
  const rows = tier === 'far' ? all : all.filter((u) => u <= template.form.deck + 1e-9);
  const stations = stationsOf(tier);
  const rings = stations.map((s) => hullRing(template, s, rows));
  loftSolid(
    builder,
    styles.hull,
    rings,
    bandStyles(template, rows, styles, tier === 'far'),
    plankingUv(template, stations, rows)
  );
}

/* Each tier sinks a little deeper than the one below it, so tiers that overlap share no corner
   and stay separate solids. */
function castleRing(template: FormedTemplate, s: number, top: number, level: number): Ring3 {
  const x = hullPoint(template, s, 1)[0];
  const w = castleHalfWidth(template, s);
  const bottom = deckAt(template, s) - CASTLE_SINK - TIER_STEP * level;
  return [
    [x, bottom, w],
    [x, top, w],
    [x, top, -w],
    [x, bottom, -w],
  ];
}

/* A castle's sides are planked from its foot (0) to its top (1), in strakes as wide as the hull's
   at its middle; its top and ends stay plain. */
function buildCastle(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  castle: CastleTier,
  styles: BodyStyles
): void {
  const top = (s: number): number =>
    tier === 'far' ? tierRailAt(template, castle, s) : tierDeckAt(template, castle, s);
  const level = template.form.aftcastle.indexOf(castle) + 1;
  const samples = samplesOver(tier, castle.from, castle.to);
  const rings = samples.map((s) => castleRing(template, s, top(s), level));
  const middle = castleRing(
    template,
    (castle.from + castle.to) / 2,
    top((castle.from + castle.to) / 2),
    level
  );
  const planking = plankingStyle(styles.hull, template.type, middle[1]![1] - middle[0]![1]);
  loftSolid(
    builder,
    styles.hull,
    rings,
    (k) => (k === 0 || k === 2 ? planking : styles.hull),
    (r, k) => [rings[r]![k]![0] + template.length / 2, k === 1 || k === 2 ? 1 : 0]
  );
}

/* The bulwarks' outer faces run the hull's strakes on from the deck to the rail: their foot, sunk
   below the deck, lies that far round the girth below the deck row. */
function bulwarkUv(
  template: FormedTemplate,
  samples: readonly number[]
): (sample: number, top: boolean) => Vec2 {
  const deckRow = 1 + loftRows(template).indexOf(template.form.deck);
  const feet = samples.map((s) => {
    const section = girthSection(template, s);
    return girthFractions(section)[deckRow]! - BULWARK.sink / girthLength(section);
  });
  return (i, top) => [
    hullPoint(template, samples[i]!, top ? 1 : template.form.deck)[0] + template.length / 2,
    top ? 1 : feet[i]!,
  ];
}

function buildBulwarks(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  styles: BodyStyles
): void {
  const step = 1 / (stationsOf(tier).length - 1);
  const from = Math.max(0, template.form.forecastle.to - step);
  const to = Math.min(1, template.form.aftcastle[0]!.from + step);
  const samples = samplesOver(tier, from, to);
  const { deck } = template.form;
  const outerFace = { style: styles.planking, uv: bulwarkUv(template, samples) };
  sideStrips(
    builder,
    styles.hull,
    samples,
    (s) => {
      const [xd, yd, zd] = hullPoint(template, s, deck);
      const [xr, yr, zr] = hullPoint(template, s, 1);
      const bottom = yd - BULWARK.sink;
      const outer = zd - BULWARK.set;
      return [
        [xd, bottom, outer],
        [xr, yr, zr],
        [xr, yr, zr - BULWARK.thickness],
        [xd, bottom, outer - BULWARK.thickness],
      ];
    },
    outerFace
  );
  sideStrips(builder, styles.rail, samples, (s) => {
    const [x, y, z] = hullPoint(template, s, 1);
    const [outer, inner] = [z + CAP_RAIL.overhang, z - BULWARK.thickness - CAP_RAIL.overhang];
    return [
      [x, y - CAP_RAIL.below, outer],
      [x, y + CAP_RAIL.above, outer],
      [x, y + CAP_RAIL.above, inner],
      [x, y - CAP_RAIL.below, inner],
    ];
  });
}

/* Wales follow the rows they band, from the first station abaft the stem to just proud of the
   transom, so their ends never lie in its plane. */
function buildWales(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  styles: HullStyles
): void {
  const samples = stationsOf(tier).slice(1);
  for (const w of template.form.wales) {
    sideStrips(builder, styles.wale, samples, (s, last) => {
      const shift = last ? WALE_GRIP : 0;
      const [xb, yb, zb] = hullPoint(template, s, w - WALE_HALF);
      const [xt, yt, zt] = hullPoint(template, s, w + WALE_HALF);
      return [
        [xb + shift, yb, zb + WALE_PROUD],
        [xt + shift, yt, zt + WALE_PROUD],
        [xt + shift, yt, zt - WALE_GRIP],
        [xb + shift, yb, zb - WALE_GRIP],
      ];
    });
  }
}

export function buildFormedHull(
  builder: MeshBuilder,
  template: FormedTemplate,
  tier: ShipTier,
  palette: HullPalette,
  edgeId: number,
  seed: number
): void {
  const hull = hullStylesOf(palette, edgeId, seed);
  const girth = girthLength(girthSection(template, 0.5));
  const styles = { ...hull, planking: plankingStyle(hull.hull, template.type, girth) };
  buildBody(builder, template, tier, styles);
  for (const castle of castleTiers(template)) buildCastle(builder, template, tier, castle, styles);
  if (tier !== 'far') {
    buildBulwarks(builder, template, tier, styles);
    buildWales(builder, template, tier, styles);
  }
  buildFittings(builder, template, tier, styles);
}
